/**
 * Real-time grocery list hook: fetches initial data, subscribes to
 * INSERT/UPDATE/DELETE for that list, cleans up on unmount, handles reconnection.
 */

import { supabase } from '@/lib/supabase';
import type { GroceryList } from '@/types';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Raw grocery_lists row from Supabase */
interface GroceryListRow {
  id: string;
  household_id: string;
  name: string;
  items: unknown;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

function parseItems(raw: unknown): GroceryList['items'] {
  if (!Array.isArray(raw)) return [];
  const addedAtToStr = (v: unknown) =>
    typeof v === 'string' ? v : new Date((v as number) ?? 0).toISOString();
  const parsed = raw.map((item, index) => {
    const o = item as Record<string, unknown>;
    return {
      id: String(o.id ?? ''),
      name: String(o.name ?? ''),
      normalizedName: String(o.normalizedName ?? o.name ?? ''),
      quantity: o.quantity != null ? Number(o.quantity) : undefined,
      unit: o.unit != null ? String(o.unit) : undefined,
      category: String(o.category ?? 'other'),
      checked: Boolean(o.checked),
      addedByUserId: String(o.addedByUserId ?? ''),
      sourceRecipes: Array.isArray(o.sourceRecipes) ? (o.sourceRecipes as string[]) : undefined,
      notes: o.notes != null ? String(o.notes) : undefined,
      addedAt: addedAtToStr(o.addedAt),
      checkedAt: o.checkedAt != null ? String(o.checkedAt) : undefined,
      position: typeof o.position === 'number' ? o.position : index,
    };
  });
  parsed.sort(
    (a, b) => a.position - b.position || a.addedAt.localeCompare(b.addedAt) || a.id.localeCompare(b.id)
  );
  return parsed;
}

function rowToList(row: GroceryListRow): GroceryList {
  return {
    id: row.id,
    household_id: row.household_id,
    name: row.name,
    items: parseItems(row.items),
    created_by_user_id: row.created_by_user_id,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

const REALTIME_DEBUG = __DEV__;

/** List ids we've already backfilled position for (one-time per list) */
const backfilledListIds = new Set<string>();

function itemsToRowForBackfill(items: GroceryList['items']): unknown[] {
  return items.map((item, index) => ({
    id: item.id,
    name: item.name,
    normalizedName: item.normalizedName,
    quantity: item.quantity ?? null,
    unit: item.unit ?? null,
    category: item.category,
    checked: item.checked,
    addedByUserId: item.addedByUserId,
    sourceRecipes: item.sourceRecipes ?? null,
    notes: item.notes ?? null,
    addedAt: item.addedAt,
    checkedAt: item.checkedAt ?? null,
    position: index,
  }));
}

export interface UseGroceryListResult {
  list: GroceryList | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Pass to useGroceryListMutations for optimistic updates */
  setList: React.Dispatch<React.SetStateAction<GroceryList | null>>;
}

/**
 * Fetches a single grocery list and subscribes to real-time changes.
 * - Uses unique channel per list: grocery-list-${listId}
 * - Handles INSERT, UPDATE, DELETE (no DB writes in handler)
 * - Cleans up subscription on unmount
 * - Refetches on network reconnection (NetInfo)
 */
export function useGroceryList(listId: string | undefined): UseGroceryListResult {
  const [list, setList] = useState<GroceryList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const subscribedRef = useRef(false);

  const refetch = useCallback(async () => {
    if (!listId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('grocery_lists')
        .select('*')
        .eq('id', listId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No rows returned - list deleted
          setList(null);
          return;
        }
        throw fetchError;
      }
      if (data) {
        const row = data as GroceryListRow;
        const list = rowToList(row);
        setList(list);

        // One-time backfill: if any item is missing position, persist renumbered positions
        const rawItems = row.items;
        const needsBackfill =
          Array.isArray(rawItems) &&
          rawItems.length > 0 &&
          rawItems.some((it: unknown) => typeof (it as Record<string, unknown>).position !== 'number') &&
          !backfilledListIds.has(listId);
        if (needsBackfill) {
          backfilledListIds.add(listId);
          const payload = itemsToRowForBackfill(list.items);
          await supabase
            .from('grocery_lists')
            .update({ items: payload, updated_at: new Date().toISOString() })
            .eq('id', listId);
        }
      } else {
        setList(null);
      }
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      setError(e);
      setList(null);
      if (REALTIME_DEBUG) {
        console.log('[useGroceryList] Initial fetch error:', e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [listId]);

  // Initial fetch
  useEffect(() => {
    if (!listId) {
      setList(null);
      setLoading(false);
      setError(null);
      return;
    }
    refetch();
  }, [listId, refetch]);

  // Real-time subscription: single channel per listId, cleanup on unmount
  useEffect(() => {
    if (!listId) return;

    const channelName = `grocery-list-${listId}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'grocery_lists', filter: `id=eq.${listId}` },
        (payload) => {
          if (REALTIME_DEBUG) {
            console.log('[useGroceryList] Realtime event:', payload.eventType, listId);
          }
          switch (payload.eventType) {
            case 'DELETE':
              setList(null);
              return;
            case 'INSERT':
            case 'UPDATE': {
              const newRow = payload.new as GroceryListRow;
              if (newRow) {
                setList(rowToList(newRow));
              }
              return;
            }
            default:
              break;
          }
        }
      )
      // TODO Phase 2: Add presence tracking
      // .on('presence', { event: 'sync' }, () => {
      //   const state = channel.presenceState();
      //   setPresence(state); // Show "Alex is viewing"
      // })
      .subscribe((status, err) => {
        if (REALTIME_DEBUG) {
          console.log('[useGroceryList] Subscription status:', status, err?.message);
        }
        if (status === 'CHANNEL_ERROR' && err) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
        if (status === 'SUBSCRIBED') {
          subscribedRef.current = true;
        }
      });

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
      subscribedRef.current = false;
      if (REALTIME_DEBUG) console.log('[useGroceryList] Unsubscribed and cleaned up', listId);
    };
  }, [listId]);

  // Reconnection: refetch only when transitioning from offline to online
  const wasConnectedRef = useRef<boolean | null>(null);
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    try {
      const NetInfo = require('@react-native-community/netinfo').default;
      unsubscribe = NetInfo.addEventListener((state: { isConnected: boolean | null }) => {
        const isConnected = state.isConnected === true;
        const wasConnected = wasConnectedRef.current;
        wasConnectedRef.current = isConnected;
        if (listId && !loading && wasConnected === false && isConnected) {
          if (REALTIME_DEBUG) console.log('[useGroceryList] Network reconnected, refetching list...');
          refetch();
        }
      });
    } catch {
      // NetInfo not installed; skip reconnection
    }
    return () => {
      unsubscribe?.();
    };
  }, [listId, loading, refetch]);

  return { list, loading, error, refetch, setList };
}
